import { createContext, useContext } from "react";
import type { WidgetDataIssue, WidgetDataNotice } from "../helpers/data-issue";

// ── Collapse ─────────────────────────────────────────────────────────────────

export interface WidgetCollapseContextValue {
  /** Whether this widget instance has the collapse/expand feature enabled. */
  collapsible: boolean;
  /** The current collapsed state, owned by WidgetSlot. */
  collapsed: boolean;
  /** Toggle the collapsed state. */
  toggle: () => void;
}

export const WidgetCollapseContext = createContext<WidgetCollapseContextValue>({
  collapsible: false,
  collapsed: false,
  toggle: () => {},
});

// ── Menu ─────────────────────────────────────────────────────────────────────

export interface WidgetMenuAction {
  id: string;
  label: string;
  handler: () => void;
}

export interface WidgetMenuContextValue {
  /** Actions to show in the widget's 3-dot menu. Empty means no menu button. */
  menuActions: WidgetMenuAction[];
}

export const WidgetMenuContext = createContext<WidgetMenuContextValue>({
  menuActions: [],
});

// ── Fullscreen ──────────────────────────────────────────────────────────────

export interface WidgetFullscreenContextValue {
  /** Whether this widget instance has the fullscreen feature enabled. */
  fullscreenable: boolean;
  /** Whether this widget instance is currently fullscreen. */
  fullscreen: boolean;
  /** Toggle fullscreen for this widget instance. */
  toggle: () => void;
}

export const WidgetFullscreenContext = createContext<WidgetFullscreenContextValue>({
  fullscreenable: false,
  fullscreen: false,
  toggle: () => {},
});

// ── Content readiness ───────────────────────────────────────────────────────

export interface WidgetContentReadyContextValue {
  /** Signals that a widget's initial visual content has been rendered. */
  markContentReady: () => void;
}

export const WidgetContentReadyContext = createContext<WidgetContentReadyContextValue>({
  markContentReady: () => {},
});

// ── Data issues ─────────────────────────────────────────────────────────────

export interface WidgetDataIssueContextValue {
  /**
   * The widget has nothing to draw: a choice is missing, the source has nothing
   * for the scope, or the request failed. The `Widget` frame then keeps its header
   * and shows the shared issue state in place of the body.
   */
  issue: WidgetDataIssue | null;
  /** Results stay on screen although a newer request failed: a header notice. */
  notice: WidgetDataNotice | null;
}

/** Provided by the host (WidgetSlot) from the widget's data; empty elsewhere. */
export const WidgetDataIssueContext = createContext<WidgetDataIssueContextValue>({
  issue: null,
  notice: null,
});

// ── Embedding ───────────────────────────────────────────────────────────────

/**
 * How much of its frame a widget renders. A host section can act as a
 * widget's header ("embedded" sections of report dashboards): it renders the
 * widget once as its `body` — no border, no header bar — and once more, in the
 * section header, as its `summary` — only the widget's `headerSummary`.
 * Everywhere else a widget renders its `full` frame.
 */
export type WidgetEmbedPart = "full" | "body" | "summary";

export interface WidgetEmbedContextValue {
  part: WidgetEmbedPart;
  /**
   * In `body` part: keep the widget title as a light caption, for a section
   * holding several widgets (the section title alone does not name each one).
   */
  captioned?: boolean;
}

export const WidgetEmbedContext = createContext<WidgetEmbedContextValue>({ part: "full" });

export const WidgetTitleHiddenContext = createContext(false);

/**
 * Whether the host shows this widget's `headerSummary` somewhere (the header of
 * a collapsible widget, or of the section it is embedded in). A widget that
 * also prints those figures in its body can then skip them there.
 */
export function useHeaderSummaryShown(): boolean {
  const { collapsible } = useContext(WidgetCollapseContext);
  const { fullscreen } = useContext(WidgetFullscreenContext);
  const { part } = useContext(WidgetEmbedContext);
  return !fullscreen && (collapsible || part === "body");
}

// ── Assets ──────────────────────────────────────────────────────────────────

/**
 * Turns an image reference found in widget data (a logo, a flag, a
 * screenshot) into a URL the browser can load, or `undefined` when unknown.
 * The host decides what references mean (today: bundled demo images); an
 * absolute URL is always used as is.
 */
export type AssetResolver = (reference: string) => string | undefined;

export function isAbsoluteAssetUrl(reference: string): boolean {
  return /^(https?:|data:|blob:|\/)/.test(reference);
}

export const WidgetAssetsContext = createContext<AssetResolver>((reference) =>
  isAbsoluteAssetUrl(reference) ? reference : undefined,
);
